import React, { ReactElement, cloneElement, useRef, HTMLAttributes, FocusEvent } from "react";
import Downshift, { DownshiftProps } from "downshift";
import { matchSorter } from "match-sorter";
import Box, { BoxProps } from "../box/Box";
import Input from "../input/Input";
import CloseButton from "../CloseButton";
import Text from "../typography/Text";
import LoadingSpinner from "../LoadingSpinner";

export interface IBaseTypeaheadItem {
  value: string;
  label: string;
}

interface IRenderItemProps<T = any> {
  item: T;
  active: boolean;
}

interface ISelectProps<T extends IBaseTypeaheadItem> {
  items: T[];
  size?: "sm" | "md" | "lg" | number;
  loading?: boolean;
  renderItem: (props: IRenderItemProps<T>) => ReactElement;
  placeholder?: string;
  menuProps?: BoxProps;
  inputProps?: HTMLAttributes<HTMLInputElement>;
  getItemProps?: (globalIndex: number, option: any) => Record<string, any>;
}

export default function Typeahead<IItem extends IBaseTypeaheadItem>({
  items,
  loading = false,
  renderItem,
  placeholder,
  size = "md",
  menuProps = {},
  inputProps = {},
  getItemProps: getItemPropsFn = () => ({}),
  ...props
}: ISelectProps<IItem> & DownshiftProps<IItem>) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { onFocus, ...restInputProps } = inputProps;

  return (
    <Downshift<IItem> itemToString={item => (item ? item.label : "")} {...props}>
      {({
        getItemProps,
        getInputProps,
        isOpen,
        toggleMenu,
        highlightedIndex,
        clearSelection,
        selectedItem,
        inputValue,
      }) => (
        <div>
          <Box position="relative">
            <Input
              size={size}
              ref={inputRef}
              placeholder={placeholder}
              {...getInputProps<any>({
                onFocus: (e: FocusEvent<HTMLInputElement>) => {
                  toggleMenu();

                  if (onFocus) {
                    onFocus(e);
                  }
                },
                ...restInputProps,
              })}
            />
            {selectedItem && (
              <CloseButton
                position="absolute"
                right={0}
                top={0}
                onClick={() => {
                  clearSelection();

                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
                height="100%"
              />
            )}
          </Box>

          {isOpen && (
            <div css={{ position: "relative" }}>
              <Box
                boxShadow="0px 0px 16px rgba(0, 0, 0, 0.1)"
                as="ul"
                position="relative"
                top={0}
                left={0}
                mt={1}
                border="1px"
                zIndex={"typeahead" as any}
                maxHeight={360}
                overflowY="scroll"
                borderColor="gray.100"
                borderRadius="md"
                bg="white"
                listStyleType="none"
                display="block"
                width="100%"
                {...menuProps}
              >
                {loading ? (
                  <Box p={2}>
                    <LoadingSpinner size="sm" />
                  </Box>
                ) : (
                  matchSorter(items, inputValue || "", { keys: ["label"] }).map((item, index) =>
                    cloneElement(renderItem({ item, active: highlightedIndex === index }), {
                      ...getItemProps({
                        ...getItemPropsFn(index, item),
                        item,
                        key: item.value,
                        index,
                      }),
                    }),
                  )
                )}

                {items.length === 0 && !loading && (
                  <Text fontSize="sm" m={0} p={2}>
                    No results found.
                  </Text>
                )}
              </Box>
            </div>
          )}
        </div>
      )}
    </Downshift>
  );
}
